import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { publicBoard, DISC_LABEL } from "@/lib/scoring";

const GRID = "grid grid-cols-[36px_1fr_72px] sm:grid-cols-[80px_1fr_120px_120px_170px] lg:grid-cols-[110px_1fr_160px_160px_220px]";
const MEDAL = ["text-[#FACC15]", "text-white", "text-[#F59E0B]/80"];

function Flash({ last }) {
  return (
    <AnimatePresence>
      {last && (
        <motion.div key={last.id} data-testid="tv-last-score" initial={{ opacity: 0, y: -20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
          className="rounded-3xl bg-[#2563EB] px-6 sm:px-8 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-6">
          <span className="text-2xl lg:text-4xl font-extrabold break-words min-w-0">{last.player_name}</span>
          <span className="text-lg sm:text-xl lg:text-3xl font-bold text-white/85">{last.label} <b className="text-white">+{last.points}</b></span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Scoreboard() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  useEffect(() => {
    const load = () => publicBoard(token).then((x) => { setD(x); setErr(""); }).catch((e) => setErr(e?.response?.status === 404 ? "Scoreboard not found." : "Reconnecting…"));
    load();
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, [token]);
  const [age, setAge] = useState("");
  if (!d) return <div data-testid="tv-loading" className="min-h-[100svh] bg-[#05070D] flex items-center justify-center text-white/50 text-2xl font-bold">{err || "Loading scoreboard…"}</div>;
  const ages = [...new Set(d.leaderboard.map((r) => r.age_group).filter(Boolean))];
  const rows = d.leaderboard.filter((r) => !age || r.age_group === age).map((r, i, all) => (age ? { ...r, rank: i && all[i - 1].points === r.points ? null : i + 1 } : r));
  rows.forEach((r, i) => { if (r.rank === null) r.rank = rows[i - 1].rank; });
  const metricLabel = d.discipline === "pitching" ? "Target %" : "Hard %";
  const big = rows.length <= 8;
  return (
    <div data-testid="tv-scoreboard" className="min-h-[100svh] bg-[#05070D] text-white px-6 lg:px-14 py-8 lg:py-10 font-['Plus_Jakarta_Sans',sans-serif]">
      <header className="flex items-end justify-between gap-6">
        <div>
          <p className="text-sm lg:text-base font-bold uppercase tracking-[0.3em] text-[#60a5fa]">Rep Lab · {DISC_LABEL[d.discipline]}</p>
          <h1 data-testid="tv-title" className="mt-2 text-4xl lg:text-6xl font-extrabold tracking-tight">{d.name}</h1>
        </div>
        <span data-testid="tv-status" className={`inline-flex items-center gap-3 rounded-full px-5 py-2 text-lg font-bold ${d.status === "live" ? "bg-emerald-400/10 text-emerald-300" : "bg-white/10 text-white/60"}`}>
          {d.status === "live" && <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />}{d.status === "live" ? "LIVE" : "FINAL"}
        </span>
      </header>
      <div className="mt-6 min-h-[88px]"><Flash last={d.last} /></div>
      {ages.length > 1 && <div className="mt-4 flex gap-2">{["", ...ages].map((a) => <button key={a || "all"} data-testid={`tv-age-${a || "all"}`} onClick={() => setAge(a)} className={`rounded-full px-4 py-1.5 text-sm font-bold ${age === a ? "bg-white text-[#05070D]" : "border border-white/20 text-white/60"}`}>{a || "All ages"}</button>)}</div>}
      <div className={`mt-6 ${GRID} px-4 sm:px-6 text-sm lg:text-lg font-bold uppercase tracking-wider text-white/35`}>
        <span>#</span><span>Player</span><span className="hidden sm:block text-right">Reps · {metricLabel}</span><span className="hidden sm:block text-right">Avg</span><span className="text-right">Points</span>
      </div>
      <motion.div layout className="mt-2 space-y-2">
        {rows.map((r, i) => (
          <motion.div layout key={r.key} transition={{ type: "spring", stiffness: 300, damping: 30 }} data-testid={`tv-row-${i}`}
            className={`${GRID} gap-2 items-center rounded-2xl px-4 sm:px-6 ${big ? "py-5" : "py-3"} ${r.rank === 1 && r.points > 0 ? "bg-white/[0.08] ring-1 ring-[#FACC15]/40" : "bg-white/[0.04]"}`}>
            <span className={`${big ? "text-4xl" : "text-2xl"} font-extrabold ${MEDAL[r.rank - 1] || "text-white/40"}`}>{r.rank}</span>
            <span className={`${big ? "text-xl sm:text-3xl lg:text-5xl" : "text-lg sm:text-2xl lg:text-3xl"} font-extrabold break-words min-w-0`}>{r.name}{r.improving && <span data-testid={`tv-improving-${i}`} className="ml-3 align-middle text-sm lg:text-lg font-bold text-emerald-300">▲ above career avg</span>}</span>
            <span className={`hidden sm:block text-right ${big ? "text-2xl lg:text-3xl" : "text-xl"} font-bold text-white/60`}>{r.reps} · {r.metric}%</span>
            <span className={`hidden sm:block text-right ${big ? "text-2xl lg:text-3xl" : "text-xl"} font-bold text-white/60`}>{r.avg}</span>
            <span className={`text-right ${big ? "text-3xl sm:text-4xl lg:text-6xl" : "text-2xl sm:text-3xl"} font-extrabold text-[#60a5fa] tabular-nums`}>{r.points}</span>
          </motion.div>
        ))}
      </motion.div>
      {err && <p className="mt-6 text-white/40">{err}</p>}
    </div>
  );
}
